import {
	computed,
	watch,
	ref,
	onBeforeUpdate,
	onMounted,

} from 'vue';
import { useStore } from 'vuex';

import { facetGroupNames, itemListIdAndName } from 'shared/settings/constants';
import AutoSuggestService from 'shared/services/autoSuggest.service';
import ShortCodeSearchService from "shared/services/shortCodeSearch.service";
import { track_view_item_list } from 'shared/services/tracking/productTrackingService';
import { prepareSearchPhrase } from 'shared/utils/queryParams.utils';
import useProductListLabels from 'shared/components/product/composables/useProductListLabels.js';


// Refs
const
	autoSuggestInProgress = ref(false),
	autoSuggestSource = ref(null),
	didYouMeanItems = ref([]),
	currentlySearchingFor = ref(''),
	showFilters = ref(false),
	hideResult = ref(false),
	keyWordSearchInProgress = ref(false),
	selectedSuggestionIndex = ref(-1),
	showNoMatchingShortCodeError = ref(false),
	suggestions = ref([]),
	suggestionPropertyGroupDelimiter = ref(''),
	suggestionsEcommerceTrackingData = ref(null),
	maxSizeOfTitle = ref(27),
	displayManufacturerLogo = ref(false),
	headingTitle = ref(''),
	manufacturerInfo = ref(null),
	isSearching = ref(false),
	observer = ref(null),
	term = ref(null),
	displaySearchOnHero = ref(false),

	apiUri = ref(''),
	shortCodeApiUri = ref(''),
	autoSuggestApiUri = ref(''),
	pageProductCategory = ref(''),
	enableManufacturerTabs = ref(false);

let
	areWatchersInited,
	onMountedConfigured = false;

function findUncheckedItem(item) {
	return item.find(({ checked }) => !checked);
}

function findCheckedItem(item) {
	return item.find(({ checked }) => checked);
}

async function shortCodeSearch(payload) {
	const query = payload.query?.trim();
	if (!query) return;

	const response = await ShortCodeSearchService.getShortCode(`${shortCodeApiUri.value}${query}`);

	if (response?.data?.items?.length > 0) {
		location.href = `${location.protocol}//${location.host}${response.data.items[0].url}`;
	}

	return response;
}

function registerWithObserver(htmlElement) {
	observer.value.observe(htmlElement);
}

function unregisterFromObserver(htmlElement) {
	observer.value.unobserve(htmlElement);
}

function intializeObserver(callback, options) {
	if (observer.value) {
		observer.value.disconnect();
	}
	// Add an intersectionobserver to ensure product cards are only tracked as they are viewed
	// Call this in the created lifecycle hook if you want child components
	// to be able to register themselves
	observer.value = new IntersectionObserver(
		callback,
		options,
	);
}

export default function useProductList(props, store, isHcpList = false) {
	store = store || useStore();

	const {
		labels,
		getApplyText,
	} = useProductListLabels(props);

	if (props) {
		headingTitle.value = props.vm.CurrentPage.CategoryName;
		displaySearchOnHero.value = props.vm.BaseViewModel.HasHeroHeader;

		apiUri.value = props.apiUri;
		shortCodeApiUri.value = props.shortCodeApiUri;
		autoSuggestApiUri.value = props.autoSuggestApiUri;
		pageProductCategory.value = props.vm.CurrentPage.ProductCategory;
		enableManufacturerTabs.value = props.enableTabs;
	}

	// store getters
	const
		primaryCategoriesNameActiveItem = computed(() => store.getters.getPrimaryCategoriesNameActiveItem),
		showPrimaryCategoryFacets = computed(() => store.getters.getShowPrimaryCategoryFacets),
		facetGroups = computed(() => store.getters.getFacetGroups),
		pagination = computed(() => store.getters.getPagination),
		anyFacets = computed(() => store.getters.getAnyFacets),
		products = computed(() => store.getters.getResults),
		apiSearchResult = computed(() => store.getters.getApiSearchResult),
		searchTerm = computed(() => store.getters.getSearchTerm),
		currency = computed(() => store.getters.getCurrency),
		productCategory = computed(() => store.getters.getProductCategory),
		facetsGroupsOnlyPrimaryCategory = computed(() => store.getters.getFacetGroupsOnlyPrimaryCategory),
		facetsGroupWithoutPrimaryCategory = computed(() => store.getters.getFacetGroupsProductPage),
		loading = computed(() => store.getters.getLoading),
		selectedManufacturer = computed(() => store.getters.getSelectedManufacturer),
		searchQueryString = computed(() => store.getters.getSearchQueryString),
		showAll = computed(() => store.getters.getShowAll);

	const isPrimaryCategoryFacets = computed(() => showPrimaryCategoryFacets.value && !loading.value);

	const isResultOnlyWithCampaign = computed(() => {
		const isProductListWithCampaignCard = products.value.some((item) => item.productListViewModelType !== 'SearchResultItem');
		const isProductListHasOnlyOneItem = products.value.length === 1;
		return isProductListHasOnlyOneItem && isProductListWithCampaignCard;
	});

	const hasCategories = computed(() => {
		if (getCurrentCategory?.value?.group?.viewAllClicked) {
			return false;
		}
		return getCurrentCategory?.value?.value && checkedCategories.value;
	});

	const checkedCategories = computed(() => {
		const getAllGroupsWithPageOrders = facetGroups.value.filter(group => group.pageOrder > 0 && group.isPage);
		const findAllCheckedGroups = filterCheckedItems(getAllGroupsWithPageOrders);

		if (isResultOnlyWithCampaign.value) {
			return false;
		}

		return getAllGroupsWithPageOrders.length !== findAllCheckedGroups.length;
	});

	const getCurrentCategory = computed(() => {
		const getFirstCategory = getFacetWithPageOrderOneAndPageCheckedOnly();
		const isNotCheckedPrimaryCategory = getFilteredCategory(getFirstCategory);

		if (isNotCheckedPrimaryCategory.length) {
			return isNotCheckedPrimaryCategory ? { group: isNotCheckedPrimaryCategory[0], value: true } : { group: [], value: false };
		}

		const nextCategories = facetGroups.value.filter(group => group.isPage);
		const isNotCheckedNextCategory = getFilteredCategory(nextCategories);

		if (isNotCheckedNextCategory.length) {
			const lowest = isNotCheckedNextCategory.reduce((previous, current) => {
				return current.pageOrder < previous.pageOrder ? current : previous;
			});

			return lowest ? { group: lowest, value: true } : { group: [], value: false };
		}

		return { group: [], value: false }
	});

	const getManufacturerLogo = computed(() =>
		facetsGroupsOnlyPrimaryCategory.value[0]?.id === facetGroupNames.MANUFACTURER_FACET ? findCheckedItem(facetsGroupsOnlyPrimaryCategory.value[0]?.data) : ''
	);

	const showProductList = computed(() =>
	      (pagination.value && pagination.value.totalResults > 0 && !hideResult.value && !hasCategories.value) ||
		  keyWordSearchInProgress.value ||
		  showAll.value
	);

	const showViewAllLink = computed(() => {
		return !loading.value &&
			hasCategories.value &&
			!keyWordSearchInProgress.value &&
			getCurrentCategory.value.group.id === facetGroupNames.MANUFACTURER_FACET &&
			getCurrentCategory.value.group.data.length > 1 &&
			getCurrentCategory.value.group.enableViewAll &&
			!getCurrentCategory.value.group.viewAllClicked;
	})

	onBeforeUpdate(() => {
		if (getCurrentCategory?.value?.value && !isHcpList) {
			store.commit('setPrimaryCategoriesName', getCurrentCategory.value.group.id);
		}
	})

	if (!onMountedConfigured) {
		onMounted(() => {
			updateHeadingTitle();
			const params = new URLSearchParams(globalThis.location.search);
			const searchphrase = params.get('searchphrase');
			keyWordSearchInProgress.value = searchphrase;
			observer?.value?.disconnect();
		});
		onMountedConfigured = true;
	}

	// Watch
	if (!areWatchersInited) {
		areWatchersInited = true;

		watch(facetGroups, () => {
			updateHeadingTitle();
		});

		watch(() => products, () => {
			if (products.value.length === 1 && keyWordSearchInProgress.value && !isResultOnlyWithCampaign.value && !enableManufacturerTabs.value) {
				hideResult.value = true;
				globalThis.location.href = `${globalThis.location.origin}${products.value[0].url}`;
			}
		}, { deep: true });
	}

	//Functions
	function initProductListing() {
		store.commit('setApiUrl', apiUri.value);
		store.commit('setProductCategory', pageProductCategory.value);
		store.dispatch('initializeSearchFromQueryString');

		// hook the callback up with the intersection observer
		intializeObserver(onProductsObserved, {
			threshold: 0.6,
		});
	}
	if (props) {
		initProductListing();
	}

	function updateHeadingTitle() {
		let headingTitleStringKey = '';
		let headingTitleString = '';
		const params = new URLSearchParams(searchQueryString.value);
		const facetStrings = params.get('facetStrings');
		if (facetStrings) {
			const facetArray = facetStrings.split(';');
			const facetsObj = {};
			for (const facet of facetArray) {
				let [key, value] = facet.split(':');
				facetsObj[key] = value;
			}

			if (facetsObj.hasOwnProperty(facetGroupNames.MANUFACTURER_FACET) && facetsObj[facetGroupNames.MANUFACTURER_FACET].toLowerCase() != 'all') {
				displayManufacturerLogo.value = true;
			} else {
				displayManufacturerLogo.value = false;
			}

			if (facetsObj.hasOwnProperty(facetGroupNames.PRODUCT_PRIMARY_CATEGORY_FACET)) {
				headingTitleStringKey = facetsObj[facetGroupNames.PRODUCT_PRIMARY_CATEGORY_FACET];
			}

			for (const facet of facetGroups.value) {
				const category = facet.data.find(
					(c) => c.value === headingTitleStringKey
				);
				if (category) {
					headingTitleString = category.name;
					break;
				}
			}
		}

		headingTitle.value = headingTitleString || headingTitleStringKey || labels.categoryName.value;
	}



	function filterCheckedItems(list) {
		return list.filter(item => findCheckedItem(item.data));
	}

	function getFacetWithPageOrderOneAndPageCheckedOnly() {
		return facetGroups.value.filter(group => group.pageOrder === 1 && group.isPage);
	}

	function clearKeywordSearch() {
		keyWordSearchInProgress.value = false;
	}

	function getFilteredCategory(categoriesList) {
		return categoriesList.filter(({ data }) => {
			if (data.length === 1) {
				return !!findUncheckedItem(data);
			}

			const hasChecked = !!findCheckedItem(data);
			if (hasChecked) {
				return false;
			}

			return !!findUncheckedItem(data);
		});
	}

	function showAllManufacturerProducts() {
		if (getCurrentCategory.value.group.id === facetGroupNames.MANUFACTURER_FACET) {
			store.dispatch('setFacetGroupToAll', getCurrentCategory.value.group);
			store.dispatch('searchBasedOnComputedQueryString');
		}
	}

	async function onProductsObserved(productElements) {

        let observedProducts = [];
		for (const { target, isIntersecting } of productElements) {
			if (!isIntersecting) continue;
			// call the unregisterFromObserver method that comes from the observer mixin
			unregisterFromObserver(target);
			const i = Number(target.dataset.index);
			const product = products.value[i];
			product.index = i;
			observedProducts.push(product);
		}
		if (observedProducts.length > 0) {
			track_view_item_list({
				productsDto: apiSearchResult.value,
				observedProducts,
				itemListIdAndName: itemListIdAndName.PRODUCT_LIST,
			});
		}
	}

	async function handleSearch(payload) {
		autoSuggestInProgress.value = false;
		isSearching.value = true;

		// first do short code search
		const response = await shortCodeSearch(payload);

		// if no result from short code search, do keyword search
		if (!response?.data?.items?.length) {
			keyWordSearch(payload);
		}
		isSearching.value = false;
	}

	function keyWordSearch(payload) {
		if (selectedSuggestionIndex.value >= 0) {
			// we have a selected suggestion so navigate to that
			location.href = suggestions.value[selectedSuggestionIndex.value].url;
		}
		else {
			resetSuggestions();
			term.value = payload.query;
			keyWordSearchInProgress.value = payload.query;
			if (enableManufacturerTabs.value) {
				changeTab(true);
			}
			store.dispatch('keywordSearch', payload.query)
				.then(() => {
					suggestions.value = [];
				});
			store.commit('setLastSearchTerm', payload.query);
		}
	}



	async function autoSuggestSearch(payload) {
		// don't perform autosuggest in case: 1) search term is empty or 2) a keyword search is already being performed on the same term
		if (!payload.term || payload.term === term.value) {
			resetSuggestions();
			return;
		}

		// "autoSuggestApiUri.value" is an url, which already has query params added and ends with
		// "&query=", thus we only need to add a search term at the end
		autoSuggestInProgress.value = true;
		const response = await AutoSuggestService.getAutoSuggest(`${autoSuggestApiUri.value}${prepareSearchPhrase(payload.term)}`);

		if (response?.data) {
			suggestionsEcommerceTrackingData.value = response.data.globalTrackingParameters;
			suggestions.value = [];
			suggestionPropertyGroupDelimiter.value = response.data.dataFieldsDelimiter;

			for (const result of response.data.items) {
				suggestions.value.push(result);
			}



			store.commit('setLastSearchTerm', payload.term);

			autoSuggestInProgress.value = false;

			if (response.data.didYouMeanItems) {
				didYouMeanItems.value = response.data.didYouMeanItems;
			} else {
				didYouMeanItems.value = [];
			}

			if (response.data.items) {
				store.dispatch('trackSearchStats', { searchQuery: payload.term, itemCount: response.data.items.length });
			}
		} else {
			didYouMeanItems.value = [];
		}
	}

	function resetSuggestions() {
		suggestions.value = [];
		didYouMeanItems.value = [];
		selectedSuggestionIndex.value = -1;
		showNoMatchingShortCodeError.value = false;
		term.value = '';
	}

	function descSuggestion() {
		selectedSuggestionIndex.value -= 1;
		if (selectedSuggestionIndex.value < 0) {
			selectedSuggestionIndex.value = suggestions.value.length - 1;
		}
	}

	function ascSuggestion() {
		selectedSuggestionIndex.value += 1;
		if (selectedSuggestionIndex.value >= suggestions.value.length) {
			selectedSuggestionIndex.value = 0;
		}
	}

    function paginationClickHandler(pageNumber) {
        store.dispatch('paginationChange', pageNumber);
    }

	function facetChange() {
		store.dispatch('setCurrentPage', 1);
	}

	function toggleFilters() {
		document.querySelector('body').classList.toggle('fixed-scroll');

		showFilters.value = !showFilters.value;
		const filter = document.querySelector('.facet__list__select__label--not-disabled');
		if (filter != null) {
			filter.focus();
		}
	}

	function resetFacets() {
		store.dispatch('resetNonPageFacets');
	}

	function changeTab(event) {
		store.dispatch('setFacetGroupToAll', { id: "Manufacturer"});
		store.commit('removeNonPageFacets');
		store.commit('setCorporateOnlyParam', event);
		store.dispatch('searchBasedOnComputedQueryString');
	}

	return {
		autoSuggestSource,
		autoSuggestInProgress,
		didYouMeanItems,
		currentlySearchingFor,
		showFilters,
		hideResult,
		keyWordSearchInProgress,
		keyWordSearch,
		selectedSuggestionIndex,
		showNoMatchingShortCodeError,
		suggestions,
		suggestionPropertyGroupDelimiter,
		suggestionsEcommerceTrackingData,
		maxSizeOfTitle,
		displayManufacturerLogo,
		headingTitle,
		term,
		manufacturerInfo,
		isSearching,
		primaryCategoriesNameActiveItem,
		showPrimaryCategoryFacets,
		facetGroups,
		pagination,
		anyFacets,
		products,
		apiSearchResult,
		searchTerm,
		currency,
		productCategory,
		facetsGroupsOnlyPrimaryCategory,
		facetsGroupWithoutPrimaryCategory,
		loading,
		labels,
		findUncheckedItem,
		findCheckedItem,
		filterCheckedItems,
		isPrimaryCategoryFacets,
		getApplyText,
		isResultOnlyWithCampaign,
		hasCategories,
		checkedCategories,
		getCurrentCategory,
		getManufacturerLogo,
		showProductList,
		showViewAllLink,
		registerWithObserver,
		facetChange,
		toggleFilters,
		resetFacets,
		handleSearch,
		descSuggestion,
		resetSuggestions,
		ascSuggestion,
		autoSuggestSearch,
		showAllManufacturerProducts,
		displaySearchOnHero,
		selectedManufacturer,
		paginationClickHandler,
		clearKeywordSearch,
		changeTab,
	}
};
